import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { agentLogin } from "../utils/api";
import { setRaygunUser, clearRaygunUser } from "../lib/raygun";

const AgentAuthContext = createContext();

export const useAgentAuth = () => {
  const context = useContext(AgentAuthContext);
  if (!context) {
    throw new Error("useAgentAuth must be used within an AgentAuthProvider");
  }
  return context;
};

export const AgentAuthProvider = ({ children }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  const applyAgent = useCallback((agentData) => {
    setAgent(agentData);
    if (agentData) {
      setRaygunUser(agentData);
    } else {
      clearRaygunUser();
    }
  }, []);

  const verifyAgentToken = useCallback(async () => {
    try {
      const response = await agentLogin.verifySession();
      applyAgent(response.data.agent);
      return { success: true, agent: response.data.agent };
    } catch (_error) {
      // Legacy bearer tokens may still be read by the API interceptor during
      // migration, but invalid credentials are never retained.
      localStorage.removeItem("agentToken");
      applyAgent(null);
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [applyAgent]);

  useEffect(() => {
    const initializeAuth = async () => {
      // Cookie is the primary session. The shared API interceptor may attach a
      // pre-existing localStorage bearer token as a read-only migration fallback.
      await verifyAgentToken();
    };

    initializeAuth();
  }, [verifyAgentToken]);

  const requestMagicLink = useCallback(async (email, returnTo) => {
    setMagicLinkLoading(true);
    try {
      const response = await agentLogin.request(email, returnTo);
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          "We couldn’t request a sign-in email right now. Try again.",
      };
    } finally {
      setMagicLinkLoading(false);
    }
  }, []);

  const verifyMagicLink = useCallback(async (token) => {
    try {
      const response = await agentLogin.exchange(token);
      applyAgent(response.data.agent);
      setLoading(false);
      return { success: true };
    } catch (_error) {
      applyAgent(null);
      setLoading(false);
      return {
        success: false,
        error: "This sign-in link is no longer available.",
      };
    }
  }, [applyAgent]);

  const logout = useCallback(async () => {
    try {
      await agentLogin.logout();
    } catch (error) {
      console.warn("Failed to notify server of logout:", error);
    } finally {
      localStorage.removeItem("agentToken");
      applyAgent(null);
    }
  }, [applyAgent]);

  const isAgent = () => Boolean(agent);

  const clearAgentAuth = () => {
    localStorage.removeItem("agentToken");
    applyAgent(null);
  };

  const getAgentInfo = () => agent || null;

  const value = {
    agent,
    loading,
    magicLinkLoading,
    requestMagicLink,
    verifyMagicLink,
    logout,
    isAgent,
    getAgentInfo,
    verifyAgentToken,
    clearAgentAuth,
  };

  return (
    <AgentAuthContext.Provider value={value}>
      {children}
    </AgentAuthContext.Provider>
  );
};
